import { YouTubePolicyPanel } from '../ApplicationPolicy';
import { useEffect, useState, type ReactNode } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import type {
  PublishingApproval,
  PublishingDelivery,
  PublishingMedia,
  PublishingPrepareInput,
  PublishingVariant,
  PublishingVisibility,
  PublishingValidation,
} from "../../../../shared/publishing-studio";
import type {
  YouTubeMetrics,
  YouTubeReport,
  YouTubeReportRow,
} from "../../../../shared/youtube-studio";
import { api } from "../../lib/api";
import type { PublicProviderResourceAssignment } from "../../../../shared/types";
import { connectGoogleCapability, useConnectionResourceAssignments } from "../../hooks/useAccountConnections";
import {
  studioBase,
  usePreparePublishing,
  usePublishingDeliveries,
  usePublishingDelivery,
  usePublishingDrafts,
  usePublishingDraft,
  usePublishingMediaItem,
  usePublishingMedia,
  usePublishingTargets,
  usePublishingVariant,
  usePublishingVariants,
  useRefreshYouTube,
  useStudioMutation,
  useYouTubeReport,
  useYouTubeVideo,
  useYouTubeVideos,
} from "../../hooks/usePublishingStudio";
import "./Studio.css";

const enc = encodeURIComponent;
const base = (brand: string) => `/brand/${enc(brand)}`;
const readable = (s: string) => s.replaceAll("_", " ");
function releaseAction(visibility: PublishingVisibility, scheduled: boolean) {
  return scheduled
    ? "Schedule public release"
    : visibility === "private"
      ? "Upload privately"
      : visibility === "unlisted"
        ? "Publish unlisted"
        : "Publish publicly";
}
function releaseNotice(visibility: PublishingVisibility, scheduled: boolean) {
  return scheduled
    ? "The video uploads privately with your requested public release time. YouTube controls processing and release."
    : visibility === "private"
      ? "The video uploads privately. This submission does not request a public release."
      : `This submission requests an immediate ${visibility} upload. YouTube may expose it as soon as processing permits. Public and unlisted uploads require verified project eligibility and channel permissions.`;
}

function timestamp(value: string | null) {
  return value ? new Date(value).toLocaleString() : "Not available";
}
function ErrorNotice({ error }: { error: unknown }) {
  return error ? (
    <div role="alert" className="studio-notice studio-error">
      {error instanceof Error
        ? error.message
        : "The request could not be completed."}
    </div>
  ) : null;
}
function Loading({ children = "Loading…" }: { children?: ReactNode }) {
  return (
    <div className="studio-empty" role="status">
      {children}
    </div>
  );
}
function Heading({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children?: ReactNode;
}) {
  return (
    <div className="studio-heading">
      <div>
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>
      <div className="studio-row">{children}</div>
    </div>
  );
}
function Badge({ value }: { value: string }) {
  return (
    <span
      className={`studio-badge ${["published", "complete", "private", "scheduled"].includes(value) ? "good" : ["failed", "reconciliation_required", "partial", "unverified", "private_only"].includes(value) ? "warn" : ""}`}
    >
      {readable(value)}
    </span>
  );
}
function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="studio-card">
      <h2>{title}</h2>
      <div className="studio-pad">{children}</div>
    </section>
  );
}
function Pagination({
  offset,
  more,
  onChange,
}: {
  offset: number;
  more: boolean;
  onChange: (offset: number) => void;
}) {
  return (
    <div className="studio-pagination">
      <span className="studio-muted">Page {Math.floor(offset / 20) + 1}</span>
      <div className="studio-row">
        <button
          className="studio-button"
          disabled={!offset}
          onClick={() => onChange(Math.max(0, offset - 20))}
        >
          Previous
        </button>
        <button
          className="studio-button"
          disabled={!more}
          onClick={() => onChange(offset + 20)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export function PublishingStudioLayout() {
  const { brandId = "" } = useParams();
  return (
    <div className="publishing-studio">
      <nav className="studio-nav" aria-label="Publishing navigation">
        <NavLink to={`${base(brandId)}/publishing`}>Publishing</NavLink>
        <NavLink to={`${base(brandId)}/youtube/videos`}>YouTube videos</NavLink>
        <NavLink to={`${base(brandId)}/youtube/analytics`}>
          Channel analytics
        </NavLink>
        <NavLink to={`${base(brandId)}/youtube/readiness`}>Connections</NavLink>
      </nav>
      <div className="studio-content">
        <YouTubePolicyPanel compactAccepted />
        <Outlet />
        <footer className="studio-footer">
          <a
            href="https://helloconvo.ai/privacy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy policy
          </a>
          <a
            href="https://helloconvo.ai/terms"
            target="_blank"
            rel="noreferrer"
          >
            Terms
          </a>
          <Link to="/settings/connections">Manage connections</Link>
          <span>YouTube data and publishing use your connected channel.</span>
        </footer>
      </div>
    </div>
  );
}

export function PublishingWorkspacePage() {
  const { brandId = "" } = useParams();
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const variants = usePublishingVariants(brandId, offset);
  const deliveries = usePublishingDeliveries(brandId);
  const items =
    variants.data?.items.filter((v) =>
      v.title.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];
  return (
    <>
      <Heading
        title="Publishing"
        subtitle="Prepare destination variants, review the exact content, and follow delivery."
      >
        <Link className="studio-button" to={`${base(brandId)}/review`}>
          LinkedIn, Bluesky & Facebook review
        </Link>
        <Link
          className="studio-button primary"
          to={`${base(brandId)}/publishing/new`}
        >
          New content
        </Link>
      </Heading>
      <ErrorNotice error={variants.error ?? deliveries.error} />
      <section className="studio-card">
        <div className="studio-filters">
          <input
            aria-label="Search prepared content on this page"
            className="studio-filter"
            placeholder="Search prepared content…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="studio-muted">
            YouTube · first supported destination
          </span>
        </div>
        {variants.isPending ? (
          <Loading />
        ) : (
          <div className="studio-table-wrap">
            <table className="studio-table">
              <thead>
                <tr>
                  <th>Content</th>
                  <th>Destination</th>
                  <th>Review</th>
                  <th>Updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <strong>{v.title}</strong>
                      <small className="studio-muted">
                        {" "}
                        · revision {v.revision}
                      </small>
                    </td>
                    <td>{v.target.label}</td>
                    <td>
                      <Badge value={v.approval ? "approved" : "draft"} />
                    </td>
                    <td>{timestamp(v.updated_at)}</td>
                    <td>
                      <Link
                        to={`${base(brandId)}/publishing/variants/${enc(v.id)}/review`}
                      >
                        Review →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!items.length && (
              <Loading>
                No prepared content yet. Start with a canonical content draft
                and a video asset.
              </Loading>
            )}
          </div>
        )}
        <Pagination
          offset={offset}
          more={variants.data?.has_more ?? false}
          onChange={setOffset}
        />
      </section>
      <h2 className="studio-subheading">Recent deliveries</h2>
      <section className="studio-card">
        <div className="studio-table-wrap">
          <table className="studio-table">
            <thead>
              <tr>
                <th>Delivery</th>
                <th>Status</th>
                <th>Destination video</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.data?.items.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Link
                      to={`${base(brandId)}/publishing/deliveries/${enc(d.id)}`}
                    >
                      {d.id.slice(0, 8)} →
                    </Link>
                  </td>
                  <td>
                    <Badge value={d.state} />
                  </td>
                  <td>{d.external_video_id ?? "Pending"}</td>
                  <td>{timestamp(d.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!deliveries.isPending && !deliveries.data?.items.length && (
          <Loading>No deliveries yet.</Loading>
        )}
      </section>
    </>
  );
}

export function YouTubeComposerPage() {
  const { brandId = "", variantId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const targets = usePublishingTargets(brandId);
  const drafts = usePublishingDrafts(brandId);
  const media = usePublishingMedia(brandId);
  const original = usePublishingVariant(brandId, variantId);
  const prepare = usePreparePublishing(brandId);
  const update = useStudioMutation<
    PublishingVariant,
    PublishingPrepareInput & { revision: number }
  >(brandId, `/publishing/variants/${enc(variantId ?? "")}`, "PUT");
  const [contentId, setContentId] = useState(params.get("content_id") ?? "");
  const [assignment, setAssignment] = useState("");
  const [mediaId, setMediaId] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [category, setCategory] = useState("22");
  const [kids, setKids] = useState<boolean | null>(null);
  const [synthetic, setSynthetic] = useState<boolean | null>(null);
  const [generatedThumbnail, setGeneratedThumbnail] = useState(false);
  const [visibility, setVisibility] = useState<PublishingVisibility>("private");
  const [scheduled, setScheduled] = useState(false);
  const [publishAt, setPublishAt] = useState("");
  const [rights, setRights] = useState(false);
  const [usageRights, setUsageRights] = useState<"owned" | "licensed" | "">("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<unknown>(null);
  useEffect(() => {
    const v = original.data;
    if (!v) return;
    setContentId(v.content_id);
    setAssignment(v.assignment_id);
    setMediaId(v.media_id);
    setThumbnail(v.thumbnail_media_id ?? "");
    setTitle(v.title);
    setDescription(v.description);
    setTags(v.tags?.join(", ") ?? "");
    setCategory(v.category_id ?? "22");
    setKids(v.made_for_kids);
    setSynthetic(v.contains_synthetic_media);
    setGeneratedThumbnail(v.accept_generated_thumbnail);
    setVisibility(v.visibility);
    setScheduled(!!v.publish_at);
    if (v.publish_at) {
      const d = new Date(v.publish_at);
      setPublishAt(
        new Date(d.getTime() - d.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16),
      );
    }
  }, [original.data]);
  const selectedDraft = usePublishingDraft(brandId, contentId);
  const selectedMedia = usePublishingMediaItem(brandId, mediaId);
  const selectedThumbnail = usePublishingMediaItem(brandId, thumbnail);
  const draftItems = [...(drafts.data?.items ?? [])];
  if (
    selectedDraft.data &&
    !draftItems.some((d) => d.content_id === selectedDraft.data!.content_id)
  )
    draftItems.unshift(selectedDraft.data);
  const mediaItems = [...(media.data?.items ?? [])];
  for (const item of [selectedMedia.data, selectedThumbnail.data])
    if (item && !mediaItems.some((m) => m.id === item.id))
      mediaItems.unshift(item);
  const draft = draftItems.find((d) => d.content_id === contentId);
  const target = targets.data?.find((t) => t.assignment_id === assignment);
  async function upload(file: File | undefined) {
    if (!file || !draft || !rights || !usageRights) return;
    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.set("file", file);
      form.set("content_id", draft.content_id);
      form.set("content_revision", String(draft.content_revision));
      form.set("usage_rights", usageRights);
      const response = await api.upload<{ data: PublishingMedia }>(
        `${studioBase(brandId)}/publishing/media/upload`,
        form,
        setUploadProgress,
      );
      setMediaId(response.data.id);
      await media.refetch();
    } catch (e) {
      setUploadError(e);
    } finally {
      setUploading(false);
    }
  }
  async function save() {
    if (!draft || kids === null || synthetic === null) return;
    const input: PublishingPrepareInput = {
      content_id: draft.content_id,
      content_revision: draft.content_revision,
      assignment_id: assignment,
      media_id: mediaId,
      thumbnail_media_id: thumbnail || null,
      title,
      description,
      tags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      category_id: category,
      made_for_kids: kids,
      contains_synthetic_media: synthetic,
      accept_generated_thumbnail: generatedThumbnail,
      visibility,
      publish_at: scheduled ? new Date(publishAt).toISOString() : null,
    };
    const saved = variantId
      ? await update.mutateAsync({
          ...input,
          revision: original.data!.revision,
        })
      : await prepare.mutateAsync(input);
    navigate(`${base(brandId)}/publishing/variants/${enc(saved.id)}/review`);
  }
  const validSchedule =
    !scheduled ||
    (publishAt &&
      Number.isFinite(new Date(publishAt).getTime()) &&
      new Date(publishAt).getTime() > Date.now() + 5 * 60000);
  return (
    <>
      <Heading
        title="Create your next video"
        subtitle="Save a destination variant for review before uploading to YouTube."
      />
      <ErrorNotice
        error={
          targets.error ??
          drafts.error ??
          media.error ??
          selectedDraft.error ??
          selectedMedia.error ??
          selectedThumbnail.error ??
          original.error ??
          prepare.error ??
          update.error ??
          uploadError
        }
      />
      <div className="studio-grid">
        <div className="studio-stack">
          <Card title="Content & destination">
            <label className="studio-field">
              Content draft
              <select
                value={contentId}
                onChange={(e) => {
                  setContentId(e.target.value);
                  const d = draftItems.find(
                    (d) => d.content_id === e.target.value,
                  );
                  if (d) {
                    setTitle(d.title);
                    setDescription(d.content);
                  }
                }}
              >
                <option value="">Choose a reviewed content draft</option>
                {draftItems.map((d) => (
                  <option key={d.content_id} value={d.content_id}>
                    {d.title} · revision {d.content_revision}
                  </option>
                ))}
              </select>
            </label>
            {drafts.hasNextPage && (
              <button
                className="studio-button"
                disabled={drafts.isFetchingNextPage}
                onClick={() => void drafts.fetchNextPage()}
              >
                Load more content drafts
              </button>
            )}
            {!drafts.isPending && !draftItems.length && (
              <div className="studio-notice">
                Create a content draft in{" "}
                <Link to={`${base(brandId)}/review`}>Review</Link> first.
              </div>
            )}
            <label className="studio-field">
              YouTube channel
              <select
                value={assignment}
                onChange={(e) => setAssignment(e.target.value)}
              >
                <option value="">Choose an assigned channel</option>
                {targets.data?.map((t) => (
                  <option key={t.assignment_id} value={t.assignment_id}>
                    {t.label}
                    {!t.upload_enabled
                      ? " · publishing permission required"
                      : ""}
                  </option>
                ))}
              </select>
            </label>
            {target && (
              <div className="studio-notice">
                {target.label} · channel {target.external_channel_id}
                <br />
                {target.blockers.join(" · ") ||
                  "Publishing permission available."}{" "}
                <Link to={`${base(brandId)}/youtube/readiness`}>
                  Check connection →
                </Link>
              </div>
            )}
          </Card>
          <Card title="Video & thumbnail">
            <label className="studio-field">
              Video asset
              <select
                value={mediaId}
                onChange={(e) => setMediaId(e.target.value)}
              >
                <option value="">Choose an uploaded video</option>
                {mediaItems
                  .filter((m) => m.mime_type.startsWith("video/"))
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label ?? m.id} ·{" "}
                      {(m.file_size_bytes / 1048576).toFixed(1)} MB
                    </option>
                  ))}
              </select>
            </label>
            {media.hasNextPage && (
              <button
                className="studio-button"
                disabled={media.isFetchingNextPage}
                onClick={() => void media.fetchNextPage()}
              >
                Load more media assets
              </button>
            )}
            <label className="studio-field">
              Video usage rights
              <select
                value={usageRights}
                onChange={(e) =>
                  setUsageRights(e.target.value as "owned" | "licensed" | "")
                }
              >
                <option value="">Choose the rights for this upload</option>
                <option value="owned">I own the video</option>
                <option value="licensed">
                  I have a license or permission to publish
                </option>
              </select>
            </label>
            <label className="studio-check">
              <input
                type="checkbox"
                checked={rights}
                onChange={(e) => setRights(e.target.checked)}
              />
              I own this video or have permission to publish it to the selected
              channel.
            </label>
            <label className="studio-field">
              Upload video
              <input
                type="file"
                accept="video/*"
                disabled={!draft || !rights || !usageRights || uploading}
                onChange={(e) => {
                  void upload(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
            {uploading && (
              <div role="status">
                <progress
                  className="studio-progress"
                  value={uploadProgress}
                  max={1}
                />
                Uploading asset to HelloConvo…{" "}
                {Math.round(uploadProgress * 100)}%
              </div>
            )}
            <label className="studio-field">
              Custom thumbnail (optional)
              <select
                value={thumbnail}
                onChange={(e) => setThumbnail(e.target.value)}
              >
                <option value="">Use YouTube generated thumbnail</option>
                {mediaItems
                  .filter((m) => m.mime_type.startsWith("image/"))
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label ?? m.id}
                    </option>
                  ))}
              </select>
            </label>
            {!thumbnail && (
              <label className="studio-check">
                <input
                  type="checkbox"
                  checked={generatedThumbnail}
                  onChange={(e) => setGeneratedThumbnail(e.target.checked)}
                />
                I accept a thumbnail generated by YouTube for this video.
              </label>
            )}
          </Card>
          <Card title="YouTube details">
            <label className="studio-field">
              Title
              <input
                maxLength={100}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>
            <label className="studio-field">
              Description
              <textarea
                maxLength={5000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <label className="studio-field">
              Tags (comma separated)
              <input value={tags} onChange={(e) => setTags(e.target.value)} />
            </label>
            <label className="studio-field">
              Category
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="22">People & Blogs</option>
                <option value="28">Science & Technology</option>
                <option value="27">Education</option>
                <option value="24">Entertainment</option>
              </select>
            </label>
            <fieldset>
              <legend>Is this video made for kids?</legend>
              <label className="studio-radio">
                <input
                  type="radio"
                  name="kids"
                  checked={kids === true}
                  onChange={() => setKids(true)}
                />
                Yes, made for kids
              </label>
              <label className="studio-radio">
                <input
                  type="radio"
                  name="kids"
                  checked={kids === false}
                  onChange={() => setKids(false)}
                />
                No, not made for kids
              </label>
            </fieldset>
            <label className="studio-field">
              Does this contain realistic altered or synthetic content requiring
              disclosure?
              <select
                value={synthetic === null ? "" : String(synthetic)}
                onChange={(e) =>
                  setSynthetic(
                    e.target.value === "" ? null : e.target.value === "true",
                  )
                }
              >
                <option value="">Choose a disclosure</option>
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </label>
          </Card>
        </div>
        <div className="studio-stack">
          <Card title="Preview">
            <div className="studio-preview">
              <div className="studio-empty">
                Video asset {mediaId ? "selected" : "not selected"}
              </div>
              <h3>{title || "Your video title"}</h3>
              <p className="studio-muted">
                {target?.label ?? "Choose a channel"}
              </p>
              <p style={{ whiteSpace: "pre-wrap" }}>
                {description.slice(0, 300)}
              </p>
            </div>
          </Card>
          <Card title="Release settings">
            <label className="studio-field">
              Requested visibility
              <select
                value={visibility}
                onChange={(e) =>
                  setVisibility(e.target.value as PublishingVisibility)
                }
              >
                <option value="private">Private</option>
                <option value="unlisted">Unlisted</option>
                <option value="public">Public</option>
              </select>
            </label>
            <label className="studio-check">
              <input
                type="checkbox"
                checked={scheduled}
                onChange={(e) => {
                  setScheduled(e.target.checked);
                  if (e.target.checked) setVisibility("public");
                }}
              />
              Schedule public release
            </label>
            {scheduled && (
              <label className="studio-field">
                Release time ·{" "}
                {Intl.DateTimeFormat().resolvedOptions().timeZone}
                <input
                  type="datetime-local"
                  value={publishAt}
                  onChange={(e) => setPublishAt(e.target.value)}
                />
              </label>
            )}
            <div className="studio-notice">
              {releaseNotice(visibility, scheduled)} Review shows the final
              requested settings.
            </div>
            <button
              className="studio-button primary"
              disabled={
                !draft ||
                !assignment ||
                !mediaId ||
                !title.trim() ||
                kids === null ||
                synthetic === null ||
                (!thumbnail && !generatedThumbnail) ||
                !validSchedule ||
                prepare.isPending ||
                update.isPending ||
                uploading
              }
              onClick={() => void save().catch(() => {})}
            >
              {prepare.isPending || update.isPending
                ? "Saving…"
                : "Save & review"}
            </button>
          </Card>
        </div>
      </div>
    </>
  );
}

function VariantDetails({ variant }: { variant: PublishingVariant }) {
  return (
    <dl className="studio-details">
      <div>
        <dt>Channel</dt>
        <dd>
          {variant.target.label}
          <div className="studio-mono">
            {variant.target.external_channel_id}
          </div>
        </dd>
      </div>
      <div>
        <dt>Content revision</dt>
        <dd>{variant.content_revision}</dd>
      </div>
      <div>
        <dt>Visibility</dt>
        <dd>{variant.visibility}</dd>
      </div>
      <div>
        <dt>Scheduled release</dt>
        <dd>
          {variant.publish_at
            ? timestamp(variant.publish_at)
            : "After processing"}
        </dd>
      </div>
      <div>
        <dt>Made for kids</dt>
        <dd>{variant.made_for_kids ? "Yes" : "No"}</dd>
      </div>
      <div>
        <dt>Altered / synthetic disclosure</dt>
        <dd>{variant.contains_synthetic_media ? "Yes" : "No"}</dd>
      </div>
      <div>
        <dt>Video asset</dt>
        <dd className="studio-mono">{variant.media_id}</dd>
      </div>
      <div>
        <dt>Thumbnail</dt>
        <dd className="studio-mono">
          {variant.thumbnail_media_id ?? "YouTube generated"}
        </dd>
      </div>
      <div>
        <dt>Category</dt>
        <dd>{variant.category_id}</dd>
      </div>
      <div>
        <dt>Tags</dt>
        <dd>{variant.tags?.join(", ") || "None"}</dd>
      </div>
    </dl>
  );
}
export function PublishingReviewPage() {
  const { brandId = "", variantId = "" } = useParams();
  const navigate = useNavigate();
  const variant = usePublishingVariant(brandId, variantId);
  const [checked, setChecked] = useState(false);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const validate = useStudioMutation<
    PublishingValidation,
    { revision: number }
  >(brandId, `/publishing/variants/${enc(variantId)}/validate`);
  const approve = useStudioMutation<PublishingApproval, { revision: number }>(
    brandId,
    `/publishing/variants/${enc(variantId)}/approve`,
  );
  const submit = useStudioMutation<
    PublishingDelivery,
    { revision: number; approval_id: string; idempotency_key: string }
  >(brandId, `/publishing/variants/${enc(variantId)}/submit`);
  const v = variant.data;
  async function approveAndSubmit() {
    if (!v) return;
    const check = await validate.mutateAsync({ revision: v.revision });
    if (!check.valid) return;
    const approval =
      v.approval ?? (await approve.mutateAsync({ revision: v.revision }));
    const delivery = await submit.mutateAsync({
      revision: v.revision,
      approval_id: approval.id,
      idempotency_key: idempotencyKey,
    });
    navigate(`${base(brandId)}/publishing/deliveries/${enc(delivery.id)}`);
  }
  useEffect(() => setChecked(false), [v?.digest]);
  return (
    <>
      <Heading
        title="Review your YouTube video"
        subtitle="Approve the exact revision, channel, media, and release settings."
      />
      <ErrorNotice
        error={variant.error ?? validate.error ?? approve.error ?? submit.error}
      />
      {!v ? (
        <Loading />
      ) : (
        <div className="studio-grid">
          <div className="studio-stack">
            <Card title={v.title}>
              <p style={{ whiteSpace: "pre-wrap" }}>{v.description}</p>
              <div className="studio-notice">
                Destination variant revision {v.revision} · saved{" "}
                {timestamp(v.updated_at)}
              </div>
              <VariantDetails variant={v} />
            </Card>
            <Link
              className="studio-button"
              to={`${base(brandId)}/publishing/variants/${enc(v.id)}/edit`}
            >
              Edit a new revision
            </Link>
          </div>
          <Card title="Approval & submission">
            <p className="studio-muted">
              Approval is tied to this revision. Any content, media, channel, or
              release change requires a fresh approval.
            </p>
            {v.target.blockers.length > 0 && (
              <div className="studio-notice">
                {v.target.blockers.join(" · ")}
              </div>
            )}
            {validate.data && (
              <div
                className={`studio-notice ${validate.data.valid ? "" : "studio-error"}`}
              >
                {validate.data.valid
                  ? "Current revision is ready."
                  : validate.data.blockers.join(" · ")}
              </div>
            )}
            <div className="studio-notice">
              {releaseNotice(v.visibility, !!v.publish_at)}
            </div>
            <label className="studio-check">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
              />
              I approve this content for {v.target.label}, including its video
              asset, audience declaration, and release settings.
            </label>
            <button
              className="studio-button"
              disabled={validate.isPending}
              onClick={() =>
                void validate
                  .mutateAsync({ revision: v.revision })
                  .catch(() => {})
              }
            >
              Check readiness
            </button>
            <button
              style={{ marginTop: 14 }}
              className="studio-button primary"
              disabled={
                !checked ||
                approve.isPending ||
                submit.isPending ||
                validate.isPending
              }
              onClick={() => void approveAndSubmit().catch(() => {})}
            >
              {approve.isPending || submit.isPending
                ? "Submitting…"
                : releaseAction(v.visibility, !!v.publish_at)}
            </button>
            <div className="studio-notice">
              If submission is interrupted, retry this same submission. Check
              Publishing for an existing delivery before starting a new one.
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

export function PublishingDeliveryPage() {
  const { brandId = "", deliveryId = "" } = useParams();
  const read = usePublishingDelivery(brandId, deliveryId);
  const d = read.data;
  const reconcile = useStudioMutation<
    PublishingDelivery,
    Record<string, never>
  >(brandId, `/publishing/deliveries/${enc(deliveryId)}/reconcile`);
  const cancel = useStudioMutation<PublishingDelivery, { revision: number }>(
    brandId,
    `/publishing/deliveries/${enc(deliveryId)}/cancel`,
  );
  return (
    <>
      <Heading
        title="YouTube delivery"
        subtitle="Track the acknowledged upload, processing, and actual visibility."
      >
        {d && <Badge value={d.state} />}
      </Heading>
      <ErrorNotice error={read.error ?? reconcile.error ?? cancel.error} />
      {!d ? (
        <Loading />
      ) : (
        <div className="studio-grid">
          <Card title="Delivery progress">
            <progress
              className="studio-progress"
              max={Math.max(1, d.total_bytes)}
              value={d.acknowledged_bytes}
            />
            <p className="studio-muted">
              {(d.acknowledged_bytes / 1048576).toFixed(1)} of{" "}
              {(d.total_bytes / 1048576).toFixed(1)} MB acknowledged by YouTube
            </p>
            <dl className="studio-details" style={{ marginTop: 24 }}>
              <div>
                <dt>Upload</dt>
                <dd>
                  <Badge value={d.state} />
                </dd>
              </div>
              <div>
                <dt>Processing</dt>
                <dd>{d.processing_status ?? "Pending"}</dd>
              </div>
              <div>
                <dt>Thumbnail</dt>
                <dd>{readable(d.thumbnail_status)}</dd>
              </div>
              <div>
                <dt>Requested visibility</dt>
                <dd>{d.requested_visibility}</dd>
              </div>
              <div>
                <dt>Actual visibility</dt>
                <dd>
                  {d.actual_visibility ?? "Awaiting provider confirmation"}
                </dd>
              </div>
              <div>
                <dt>Scheduled release</dt>
                <dd>
                  {d.scheduled_release_at
                    ? timestamp(d.scheduled_release_at)
                    : "Not scheduled"}
                </dd>
              </div>
            </dl>
            {d.blockers.length > 0 && (
              <div className="studio-notice">{d.blockers.join(" · ")}</div>
            )}
            {d.blockers.includes("upload_session_expired_create_new_approved_revision") && (
              <p className="studio-notice">
                This incomplete upload session expired. Create a new revision and
                obtain human approval for that exact revision and channel before submitting.
              </p>
            )}
            {d.state === "reconciliation_required" && (
              <p className="studio-notice">
                The delivery outcome is unresolved. Have a channel operator check
                the exact channel in YouTube Studio. Wait for confirmed outcome
                evidence and a human decision before resubmitting to avoid a duplicate upload.
                If cancellation was requested, cancellation is unconfirmed.
              </p>
            )}
            {d.next_action === "reconcile" && !d.blockers.includes("upload_completion_probe_limit_requires_reconciliation") && (
              <button
                className="studio-button primary"
                disabled={reconcile.isPending}
                onClick={() => void reconcile.mutateAsync({}).catch(() => {})}
              >
                Reconcile existing upload
              </button>
            )}
            {!["published", "cancelled", "failed"].includes(d.state) && (
              <button
                className="studio-button"
                disabled={cancel.isPending}
                onClick={() =>
                  void cancel
                    .mutateAsync({ revision: d.revision })
                    .catch(() => {})
                }
              >
                Cancel remaining delivery work
              </button>
            )}
            <p className="studio-muted" style={{ marginTop: 16 }}>
              Cancelling work does not remove a video already uploaded to
              YouTube.
            </p>
          </Card>
          <Card title="Provider receipt">
            <dl className="studio-details">
              <div>
                <dt>Delivery ID</dt>
                <dd className="studio-mono">{d.id}</dd>
              </div>
              <div>
                <dt>Channel ID</dt>
                <dd className="studio-mono">{d.external_channel_id}</dd>
              </div>
              <div>
                <dt>Video ID</dt>
                <dd>{d.external_video_id ?? "Not acknowledged yet"}</dd>
              </div>
              <div>
                <dt>Last update</dt>
                <dd>{timestamp(d.updated_at)}</dd>
              </div>
            </dl>
            {d.external_video_id && (
              <div className="studio-row" style={{ marginTop: 20 }}>
                <a
                  className="studio-button"
                  href={`https://studio.youtube.com/video/${enc(d.external_video_id)}/edit`}
                  target="_blank"
                  rel="noreferrer"
                >
                  YouTube Studio ↗
                </a>
                <a
                  className="studio-button"
                  href={`https://www.youtube.com/watch?v=${enc(d.external_video_id)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View video ↗
                </a>
              </div>
            )}
            <Link
              className="studio-button"
              style={{ marginTop: 20 }}
              to={`${base(brandId)}/youtube/videos`}
            >
              Video library
            </Link>
          </Card>
        </div>
      )}
    </>
  );
}

function useChannelSelection() {
  const { brandId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const targets = usePublishingTargets(brandId);
  const assignment = params.get("assignment_id") ?? "";
  useEffect(() => {
    if (!assignment && targets.data?.length === 1)
      setParams(
        (p) => {
          p.set("assignment_id", targets.data![0]!.assignment_id);
          return p;
        },
        { replace: true },
      );
  }, [assignment, targets.data, setParams]);
  return {
    brandId,
    assignment,
    targets,
    selector: (
      <label className="studio-field">
        Channel
        <select
          value={assignment}
          onChange={(e) =>
            setParams((p) => {
              p.set("assignment_id", e.target.value);
              return p;
            })
          }
        >
          <option value="">Choose a channel</option>
          {targets.data?.map((t) => (
            <option key={t.assignment_id} value={t.assignment_id}>
              {t.label}
            </option>
          ))}
        </select>
      </label>
    ),
  };
}

export function YouTubeLibraryPage() {
  const { brandId, assignment, targets, selector } = useChannelSelection();
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const videos = useYouTubeVideos(brandId, assignment, search, offset);
  const refresh = useRefreshYouTube(brandId);
  return (
    <>
      <Heading
        title="YouTube videos"
        subtitle="Your channel inventory, including videos published outside HelloConvo."
      >
        <button
          className="studio-button"
          disabled={!assignment || refresh.isPending}
          onClick={() =>
            void refresh
              .mutateAsync({ assignment_id: assignment, kind: "inventory" })
              .catch(() => {})
          }
        >
          {refresh.isPending ? "Refreshing…" : "Refresh inventory"}
        </button>
        <Link
          className="studio-button primary"
          to={`${base(brandId)}/publishing/new`}
        >
          Upload video
        </Link>
      </Heading>
      {selector}
      <ErrorNotice error={targets.error ?? videos.error ?? refresh.error} />
      {refresh.data && (
        <div className="studio-message">
          {readable(refresh.data.state)} ·{" "}
          {refresh.data.state === "completed" ||
          refresh.data.state === "partial"
            ? `${refresh.data.row_count} videos collected · `
            : ""}
          Run {refresh.data.run_id}
          {refresh.data.next_cursor && (
            <button
              className="studio-button"
              onClick={() =>
                void refresh
                  .mutateAsync({
                    assignment_id: assignment,
                    kind: "inventory",
                    cursor: refresh.data!.next_cursor!,
                  })
                  .catch(() => {})
              }
            >
              Collect next page
            </button>
          )}
        </div>
      )}
      <section className="studio-card">
        <div className="studio-filters">
          <input
            className="studio-filter"
            aria-label="Search videos"
            placeholder="Search videos…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0);
            }}
          />
        </div>
        {!assignment ? (
          <Loading>Choose an assigned channel to view its videos.</Loading>
        ) : videos.isPending ? (
          <Loading />
        ) : (
          <>
            <div className="studio-table-wrap">
              <table className="studio-table">
                <thead>
                  <tr>
                    <th>Video</th>
                    <th>Visibility</th>
                    <th>Published</th>
                    <th>Lifetime views</th>
                    <th>Origin</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {videos.data?.items.map((v) => (
                    <tr key={v.id}>
                      <td>
                        <Link
                          className="studio-title-cell"
                          to={`${base(brandId)}/youtube/videos/${enc(v.id)}?assignment_id=${enc(assignment)}`}
                        >
                          {v.thumbnail_url && (
                            <img
                              className="studio-thumb"
                              alt=""
                              src={v.thumbnail_url}
                            />
                          )}
                          <span>
                            {v.title ?? "Unavailable video"}
                            <small>{v.external_video_id}</small>
                          </span>
                        </Link>
                      </td>
                      <td>
                        <Badge value={v.visibility} />
                      </td>
                      <td>{timestamp(v.published_at)}</td>
                      <td>{v.lifetime.views?.toLocaleString() ?? "—"}</td>
                      <td>
                        {v.origin === "helloconvo" ? "HelloConvo" : "YouTube"}
                      </td>
                      <td>
                        <Link
                          to={`${base(brandId)}/youtube/videos/${enc(v.id)}/analytics?assignment_id=${enc(assignment)}`}
                        >
                          Analytics →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!videos.data?.items.length && (
              <Loading>
                No collected videos match. Refresh the inventory to collect
                channel data.
              </Loading>
            )}
          </>
        )}
        <Pagination
          offset={offset}
          more={videos.data?.next_offset != null}
          onChange={setOffset}
        />
      </section>
    </>
  );
}

export function YouTubeVideoDetailPage() {
  const { brandId, assignment, targets, selector } = useChannelSelection();
  const { videoId = "" } = useParams();
  const read = useYouTubeVideo(brandId, assignment, videoId);
  const v = read.data;
  return (
    <>
      <Heading
        title={v?.title ?? "Video details"}
        subtitle="Provider metadata and publishing provenance."
      >
        <Link
          className="studio-button primary"
          to={`${base(brandId)}/youtube/videos/${enc(videoId)}/analytics?assignment_id=${enc(assignment)}`}
        >
          View analytics
        </Link>
      </Heading>
      {selector}
      <ErrorNotice error={targets.error ?? read.error} />
      {!v ? (
        <Loading />
      ) : (
        <div className="studio-grid">
          <Card title="Video">
            <div className="studio-preview">
              {v.thumbnail_url && (
                <img alt={v.title ?? "Video thumbnail"} src={v.thumbnail_url} />
              )}
              <h3>{v.title}</h3>
              <p style={{ whiteSpace: "pre-wrap" }}>{v.description}</p>
            </div>
            <div className="studio-row" style={{ marginTop: 20 }}>
              {v.youtube_url && (
                <a
                  className="studio-button"
                  href={v.youtube_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Watch on YouTube ↗
                </a>
              )}
              <a
                className="studio-button"
                href={v.studio_url}
                target="_blank"
                rel="noreferrer"
              >
                YouTube Studio ↗
              </a>
            </div>
          </Card>
          <Card title="Details">
            <dl className="studio-details">
              <div>
                <dt>Visibility</dt>
                <dd>
                  <Badge value={v.visibility} />
                </dd>
              </div>
              <div>
                <dt>Published</dt>
                <dd>{timestamp(v.published_at)}</dd>
              </div>
              <div>
                <dt>Duration</dt>
                <dd>{v.duration ?? "Unknown"}</dd>
              </div>
              <div>
                <dt>Origin</dt>
                <dd>{v.origin}</dd>
              </div>
              <div>
                <dt>Processing</dt>
                <dd>{v.processing_status ?? "Unknown"}</dd>
              </div>
              <div>
                <dt>Observed</dt>
                <dd>{timestamp(v.observed_at)}</dd>
              </div>
              <div>
                <dt>Channel</dt>
                <dd className="studio-mono">{v.channel_id}</dd>
              </div>
              <div>
                <dt>Canonical content</dt>
                <dd className="studio-mono">
                  {v.canonical_content_id ?? "No HelloConvo provenance"}
                </dd>
              </div>
            </dl>
            <div className="studio-notice">
              Lifetime Data API counters are shown in the library. Date-range
              performance is available in Analytics.
            </div>
          </Card>
        </div>
      )}
    </>
  );
}

const metrics: [keyof YouTubeMetrics, string, string][] = [
  ["views", "Views", ""],
  ["estimated_minutes_watched", "Watch time", "min"],
  ["average_view_duration_seconds", "Average view duration", "sec"],
  ["likes", "Likes", ""],
  ["comments", "Comments", ""],
];
function metricValue(value: number | null, unit: string) {
  return value === null
    ? "—"
    : `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })}${unit ? ` ${unit}` : ""}`;
}
function ReportCoverage({ report, label, daily = false }: {
  report: YouTubeReport;
  label: string;
  daily?: boolean;
}) {
  return (
    <div className="studio-coverage" aria-label={label}>
      <strong>{label}</strong>
      {daily ? (
        <>
          <Badge value={report.coverage.state} />
          <span>Observed {report.coverage.available_start ?? "—"}–{report.coverage.available_end ?? "—"}</span>
          <span>{report.coverage.missing_dates.length} missing observed dates</span>
        </>
      ) : (
        <span>Collected summary · {report.coverage.provider_pagination_complete ? "Pagination complete" : "Pagination incomplete"}</span>
      )}
      <span>Requested {report.coverage.requested_start}–{report.coverage.requested_end}</span>
      <span>Synced {timestamp(report.coverage.synced_at)}</span>
      <span>{report.coverage.returned_video_coverage === "ranked_subset" ? "Ranked subset of videos" : report.coverage.returned_video_coverage === "channel" ? "Channel-wide coverage" : "Single video"}</span>
    </div>
  );
}

// Mirror the API's full UTC report-day boundaries; this is a navigation hint,
// while the backend continues to authorize every report and collection request.
function reportingWindows(assignment: PublicProviderResourceAssignment | undefined) {
  if (!assignment || assignment.status !== "active" || !assignment.analytics_enabled) return [];
  const day = 86400000;
  const now = Date.now();
  const from = Date.parse(assignment.valid_from);
  const until = assignment.valid_until ? Date.parse(assignment.valid_until) : Infinity;
  if (!Number.isFinite(from) || Number.isNaN(until) || from > now || until <= now) return [];
  const first = Math.ceil(from / day) * day;
  const last = Math.min(Math.floor(now / day) * day, Math.floor(until / day) * day - day);
  const date = (value: number) => new Date(value).toISOString().slice(0, 10);
  const windows: { start: string; end: string }[] = [];
  if (assignment.backfill_start && assignment.backfill_end) {
    const backfillStart = Date.parse(assignment.backfill_start);
    const backfillEnd = Math.min(Date.parse(assignment.backfill_end), last);
    if (Number.isFinite(backfillStart) && Number.isFinite(backfillEnd) && backfillStart <= backfillEnd)
      windows.push({ start: date(backfillStart), end: date(backfillEnd) });
  }
  if (first <= last) windows.push({ start: date(first), end: date(last) });
  return windows;
}
function Chart({
  rows,
  metric,
}: {
  rows: YouTubeReportRow[];
  metric: keyof YouTubeMetrics;
}) {
  const points = rows
    .filter((r) => r.date)
    .sort((a, b) => a.date!.localeCompare(b.date!));
  const values = points.map((r) => r.metrics[metric]);
  const max = Math.max(1, ...values.filter((v): v is number => v !== null));
  const segments: string[] = [];
  let segment: string[] = [];
  let previous: string | null = null;
  for (let i = 0; i < points.length; i++) {
    const date = points[i]!.date!;
    const missing =
      previous &&
      new Date(date).getTime() - new Date(previous).getTime() > 86400000;
    const value = values[i] ?? null;
    if (missing || value === null) {
      if (segment.length) segments.push(segment.join(" "));
      segment = [];
    }
    if (value !== null)
      segment.push(
        `${50 + (i / Math.max(1, points.length - 1)) * 880},${200 - (value / max) * 160}`,
      );
    previous = date;
  }
  if (segment.length) segments.push(segment.join(" "));
  return (
    <>
      <svg
        className="studio-chart"
        viewBox="0 0 960 240"
        role="img"
        aria-label={`${readable(metric)} over observed report dates; missing dates are gaps`}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i}>
            <line
              x1="50"
              x2="930"
              y1={200 - i * 40}
              y2={200 - i * 40}
              stroke="#e7edf4"
            />
            <text x="0" y={204 - i * 40}>
              {Math.round((max * i) / 4)}
            </text>
          </g>
        ))}
        {segments.map((p, i) => (
          <polyline
            key={i}
            points={p}
            fill="none"
            stroke="#2563eb"
            strokeWidth="3"
          />
        ))}
        <text x="50" y="235">
          {points[0]?.date}
        </text>
        <text x="810" y="235">
          {points.at(-1)?.date}
        </text>
      </svg>
      <p className="studio-chart-label">
        Missing dates remain gaps. No zero values are inferred.
      </p>
    </>
  );
}
export function YouTubeAnalyticsPage() {
  const { brandId, assignment, targets, selector } = useChannelSelection();
  const { videoId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [compare, setCompare] = useState(false);
  const target = targets.data?.find((t) => t.assignment_id === assignment);
  const assignments = useConnectionResourceAssignments(target?.connection_id);
  const assignedResource = assignments.data?.data.find((a) =>
    a.id === assignment && a.resource_id === target?.resource_id && a.brand_id === brandId,
  );
  const windows = !assignments.error && !assignments.isPending
    ? reportingWindows(assignedResource)
    : [];
  // The last window is current validity, preferred over any historic backfill.
  const currentWindow = windows.at(-1);
  const defaultStart = currentWindow
    ? new Date(Math.max(Date.parse(currentWindow.start), Date.parse(currentWindow.end) - 28 * 86400000)).toISOString().slice(0, 10)
    : "";
  // Implicit defaults stay out of the URL and recalculate for the selected
  // assignment. Explicit URL dates intentionally persist across channel changes.
  const start = searchParams.get("start_date") ?? defaultStart;
  const end = searchParams.get("end_date") ?? currentWindow?.end ?? "";
  const reportAssignment = start && end ? assignment : "";
  function changeDate(key: "start_date" | "end_date", value: string) {
    setSearchParams((p) => {
      p.set("start_date", start);
      p.set("end_date", end);
      p.set(key, value);
      return p;
    });
  }
  const pageScope = JSON.stringify([brandId, assignment, videoId, start, end]);
  const [page, setPage] = useState({ scope: pageScope, offset: 0 });
  // Use page one immediately, before the effect, so a new report never requests
  // the old report's offset. Persist the reset for later navigation back.
  const offset = page.scope === pageScope ? page.offset : 0;
  useEffect(() => setPage({ scope: pageScope, offset: 0 }), [pageScope]);
  const setOffset = (value: number) => setPage({ scope: pageScope, offset: value });
  const [metric, setMetric] = useState<keyof YouTubeMetrics>("views");
  const video = useYouTubeVideo(brandId, assignment, videoId ?? "");
  const summary = useYouTubeReport(
    brandId,
    reportAssignment,
    videoId ? "youtube.video_summary" : "youtube.channel_summary",
    start,
    end,
    videoId,
  );
  const rows = useYouTubeReport(
    brandId,
    reportAssignment,
    videoId ? "youtube.video_daily" : "youtube.videos",
    start,
    end,
    videoId,
    offset,
  );
  const channelDaily = useYouTubeReport(
    brandId,
    videoId ? "" : reportAssignment,
    "youtube.channel_daily",
    start,
    end,
  );
  const duration = Math.max(
    1,
    Math.round((Date.parse(end) - Date.parse(start)) / 86400000) + 1,
  );
  const previousEnd = Number.isFinite(Date.parse(start))
    ? new Date(Date.parse(start) - 86400000).toISOString().slice(0, 10)
    : start;
  const previousStart = Number.isFinite(Date.parse(previousEnd))
    ? new Date(Date.parse(previousEnd) - (duration - 1) * 86400000)
        .toISOString()
        .slice(0, 10)
    : start;
  const previous = useYouTubeReport(
    brandId,
    compare ? reportAssignment : "",
    videoId ? "youtube.video_summary" : "youtube.channel_summary",
    previousStart,
    previousEnd,
    videoId,
  );
  const previousDaily = useYouTubeReport(
    brandId,
    compare ? reportAssignment : "",
    videoId ? "youtube.video_daily" : "youtube.channel_daily",
    previousStart,
    previousEnd,
    videoId,
  );
  const comparableCoverage =
    (videoId ? rows.data : channelDaily.data)?.coverage.state === "complete" &&
    previousDaily.data?.coverage.state === "complete" &&
    previousDaily.data.coverage.provider_pagination_complete;
  // Dimensionless summaries remain partial; daily reports prove date coverage.
  const comparableSummaries = [summary.data, previous.data].every(
    (report) =>
      report?.snapshot_id &&
      report.coverage.state !== "unavailable" &&
      report.coverage.provider_pagination_complete,
  );
  const refresh = useRefreshYouTube(brandId);
  const report = summary.data;
  const daily = videoId ? rows : channelDaily;
  const comparisonError = previous.error ?? previousDaily.error;
  const historyDenied =
    comparisonError &&
    typeof comparisonError === "object" &&
    "code" in comparisonError &&
    comparisonError.code === "YOUTUBE_REPORT_WINDOW_UNAVAILABLE";
  const comparisonMessage = !compare
    ? null
    : historyDenied
      ? "Previous dates are outside this source's authorized reporting history. Choose dates within the configured history; Refresh report cannot extend it."
      : comparisonError
        ? "Comparison could not be loaded. See the query error below."
        : daily.error || summary.error
          ? "Comparison could not be loaded. See the query error above."
          : previous.isPending || previousDaily.isPending || daily.isPending
            ? "Loading comparison…"
            : !previous.data?.snapshot_id || !previousDaily.data?.snapshot_id
              ? "No collected previous-period report for these dates. Refresh report collects both periods when comparison is enabled."
              : !comparableCoverage
                ? "Comparison unavailable: incomplete daily coverage. Refresh report collects both periods; reporting delays or missing dates may still limit comparison."
                : report?.snapshot_id && !comparableSummaries
                  ? "Comparison unavailable: incomplete summary coverage. Refresh report collects both periods; summary reports must be available and fully paginated."
                  : null;
  async function collect() {
    await refresh.mutateAsync({
      assignment_id: assignment,
      kind: "analytics",
      report_key: videoId ? "youtube.video_summary" : "youtube.channel_summary",
      video_id: videoId,
      start_date: start,
      end_date: end,
    });
    await refresh.mutateAsync({
      assignment_id: assignment,
      kind: "analytics",
      report_key: videoId ? "youtube.video_daily" : "youtube.channel_daily",
      video_id: videoId,
      start_date: start,
      end_date: end,
    });
    if (!videoId)
      await refresh.mutateAsync({
        assignment_id: assignment,
        kind: "analytics",
        report_key: "youtube.videos",
        start_date: start,
        end_date: end,
      });
    if (compare) {
      await refresh.mutateAsync({
        assignment_id: assignment,
        kind: "analytics",
        report_key: videoId
          ? "youtube.video_summary"
          : "youtube.channel_summary",
        video_id: videoId,
        start_date: previousStart,
        end_date: previousEnd,
      });
      await refresh.mutateAsync({
        assignment_id: assignment,
        kind: "analytics",
        report_key: videoId ? "youtube.video_daily" : "youtube.channel_daily",
        video_id: videoId,
        start_date: previousStart,
        end_date: previousEnd,
      });
    }
  }
  const canExport = !rows.error && !rows.isPending && !!rows.data?.snapshot_id && rows.data.rows.length > 0;
  function exportCsv() {
    if (!canExport || !rows.data) return;
    const csv = [
      "date,video_id,views,minutes_watched,average_view_duration_seconds,likes,comments",
      ...rows.data.rows.map((r) =>
        [
          r.date ?? "",
          r.external_video_id ?? "",
          ...metrics.map(([m]) => r.metrics[m] ?? ""),
        ].join(","),
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "youtube-report-page.csv";
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <>
      <Heading
        title={
          videoId
            ? (video.data?.title ?? "Video analytics")
            : "Channel analytics"
        }
        subtitle={
          videoId
            ? "Date-range performance for one video."
            : "Ranked video performance for the selected period."
        }
      >
        <button
          className="studio-button"
          disabled={!canExport}
          onClick={exportCsv}
        >
          Export current page
        </button>
        <button
          className="studio-button primary"
          disabled={!reportAssignment || start > end || refresh.isPending}
          onClick={() => void collect().catch(() => {})}
        >
          {refresh.isPending ? "Collecting…" : "Refresh report"}
        </button>
      </Heading>
      {selector}
      <div className="studio-row">
        <label className="studio-field">
          Start date
          <input
            type="date"
            value={start}
            onChange={(e) => {
              changeDate("start_date", e.target.value);
            }}
          />
        </label>
        <label className="studio-field">
          End date
          <input
            type="date"
            value={end}
            onChange={(e) => {
              changeDate("end_date", e.target.value);
            }}
          />
        </label>
      </div>
      {target && assignments.isPending ? (
        <Loading>Loading allowed reporting dates…</Loading>
      ) : windows.length ? (
        <p className="studio-muted">Allowed reporting dates (UTC): {windows.map((w) => `${w.start}–${w.end}`).join("; ")}. Selected dates are checked by the server.</p>
      ) : assignment ? (
        <p className="studio-notice">Allowed reporting dates unavailable. Assignment metadata must be available to choose a default period.</p>
      ) : null}
      <ErrorNotice
        error={
          targets.error ??
          assignments.error ??
          summary.error ??
          rows.error ??
          channelDaily.error ??
          refresh.error
        }
      />
      {start > end && (
        <div className="studio-notice studio-error">
          Start date must precede the end date.
        </div>
      )}
      <label className="studio-check">
        <input
          type="checkbox"
          checked={compare}
          onChange={(e) => setCompare(e.target.checked)}
        />
        Compare previous period ({previousStart}–{previousEnd})
      </label>
      {compare && comparisonMessage && <Loading>{comparisonMessage}</Loading>}
      {compare && !historyDenied && <ErrorNotice error={comparisonError} />}
      {!summary.error && report?.snapshot_id && <ReportCoverage report={report} label="Summary collection" />}
      {!daily.error && daily.data?.snapshot_id ? <ReportCoverage report={daily.data} label="Daily coverage" daily /> : report?.snapshot_id ? <p>Daily coverage unavailable.</p> : null}
      {compare && previous.data?.snapshot_id && (
        <ReportCoverage report={previous.data} label="Previous summary collection" />
      )}
      {!assignment ? (
        <Loading>Choose a channel to read its analytics.</Loading>
      ) : !reportAssignment ? (
        <Loading>Choose reporting dates after assignment metadata is available.</Loading>
      ) : summary.isPending ? (
        <Loading />
      ) : summary.error ? (
        <Loading>
          The report could not be loaded. See the query error above.
        </Loading>
      ) : !report?.snapshot_id ? (
        <Loading>
          No collected report for these dates. Use Refresh report to collect and
          save the selected period.
        </Loading>
      ) : (
        <>
          <div className="studio-metrics">
            {metrics.map(([key, label, unit]) => (
              <div key={key} className="studio-metric">
                <span className="studio-muted">{label}</span>
                <strong>{metricValue(report.summary[key], unit)}</strong>
                {compare && !comparisonMessage && (
                  <small className="studio-muted">
                    {previous.data?.summary[key] == null ||
                    report.summary[key] == null
                      ? "Comparison unavailable: metric not reported"
                      : `Previous: ${metricValue(previous.data.summary[key], unit)}`}
                  </small>
                )}
              </div>
            ))}
          </div>
          {!videoId && (
            <div className="studio-notice">
              Channel totals come from the dimensionless YouTube Analytics
              report. The video ranking below is a provider-limited subset.
            </div>
          )}
          {
            <Card title="Performance over time">
              <label className="studio-field">
                Chart metric
                <select
                  value={metric}
                  onChange={(e) =>
                    setMetric(e.target.value as keyof YouTubeMetrics)
                  }
                >
                  {metrics.map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              {daily.isPending ? (
                <Loading>Loading daily report…</Loading>
              ) : daily.error ? (
                <Loading>Daily report could not be loaded.</Loading>
              ) : !daily.data?.snapshot_id ? (
                <Loading>
                  No collected daily report for these dates. Use Refresh report
                  to collect it.
                </Loading>
              ) : daily.data.rows.length ? (
                <Chart
                  rows={(videoId ? rows.data : channelDaily.data)!.rows}
                  metric={metric}
                />
              ) : (
                <Loading>
                  Collected daily report returned no rows for these dates.
                  Missing data is not zero.
                </Loading>
              )}
            </Card>
          }
          <section className="studio-card" style={{ marginTop: 20 }}>
            <header>{videoId ? "Daily performance" : "Video ranking"}</header>
            <div className="studio-table-wrap">
              <table className="studio-table">
                <thead>
                  <tr>
                    <th>{videoId ? "Date" : "Video"}</th>
                    {metrics.map(([key, label]) => (
                      <th key={key}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.data?.rows.map((r, i) => (
                    <tr key={r.date ?? r.video_id ?? i}>
                      <td>
                        {videoId ? (
                          r.date
                        ) : r.video_id ? (
                          <Link
                            to={`${base(brandId)}/youtube/videos/${enc(r.video_id)}/analytics?assignment_id=${enc(assignment)}&start_date=${enc(start)}&end_date=${enc(end)}`}
                          >
                            {r.title ?? r.external_video_id}
                          </Link>
                        ) : (
                          (r.title ??
                          r.external_video_id ??
                          "Unavailable video")
                        )}
                      </td>
                      {metrics.map(([key, , unit]) => (
                        <td key={key}>{metricValue(r.metrics[key], unit)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!rows.data?.rows.length && (
              <Loading>
                {rows.isPending
                  ? "Loading report rows…"
                  : rows.error
                    ? "Report rows could not be loaded."
                    : !rows.data?.snapshot_id
                      ? "No collected report rows for these dates. Use Refresh report to collect them."
                      : "Collected report returned no rows for these dates. Missing data is not zero."}
              </Loading>
            )}
            <Pagination
              offset={offset}
              more={rows.data?.next_offset != null}
              onChange={setOffset}
            />
          </section>
          <div className="studio-notice">
            {report.coverage.reporting_delay} · YouTube Analytics data is
            provisional. {!daily.error && daily.data?.snapshot_id ? `${daily.data.coverage.missing_dates.length} missing observed dates` : "Daily missing date count unavailable"}. Counts are distinct from lifetime video statistics.
          </div>
        </>
      )}
    </>
  );
}

export function YouTubeReadinessPage() {
  const { brandId = "" } = useParams();
  const targets = usePublishingTargets(brandId);
  const [error, setError] = useState<unknown>(null);
  const [connecting, setConnecting] = useState(false);
  async function connect() {
    setConnecting(true);
    try {
      await connectGoogleCapability("google.youtube.publish.write", brandId);
    } catch (e) {
      setError(e);
      setConnecting(false);
    }
  }
  return (
    <>
      <Heading
        title="YouTube connection readiness"
        subtitle="Check read access, publishing permissions, and project eligibility."
      />
      <ErrorNotice error={targets.error ?? error} />
      {targets.isPending ? (
        <Loading />
      ) : !targets.data?.length ? (
        <Card title="Connect a YouTube channel">
          <p>
            Connect Google, discover your YouTube channel, and assign it to this
            brand.
          </p>
          <Link className="studio-button primary" to="/settings/connections">
            Manage connections
          </Link>
        </Card>
      ) : (
        <div className="studio-stack">
          {targets.data.map((t) => (
            <Card key={t.assignment_id} title={t.label}>
              <dl className="studio-details">
                <div>
                  <dt>Assigned channel</dt>
                  <dd className="studio-mono">{t.external_channel_id}</dd>
                </div>
                <div>
                  <dt>Video upload permission</dt>
                  <dd>{t.upload_enabled ? "Granted" : "Required"}</dd>
                </div>
                <div>
                  <dt>Release permission</dt>
                  <dd>
                    {t.release_enabled
                      ? "Granted"
                      : "Required for visibility changes"}
                  </dd>
                </div>
                <div>
                  <dt>Custom thumbnails</dt>
                  <dd>{t.thumbnail_enabled ? "Available" : "Unavailable"}</dd>
                </div>
                <div>
                  <dt>Public upload eligibility</dt>
                  <dd>
                    <Badge value={t.public_upload_eligibility} />
                  </dd>
                </div>
              </dl>
              {t.blockers.length > 0 && (
                <div className="studio-notice">{t.blockers.join(" · ")}</div>
              )}
              <div className="studio-row" style={{ marginTop: 20 }}>
                <Link
                  className="studio-button"
                  to={`/settings/connections/${enc(t.connection_id)}`}
                >
                  Connection details
                </Link>
                {(!t.upload_enabled || !t.release_enabled) && (
                  <button
                    className="studio-button primary"
                    disabled={connecting}
                    onClick={() => void connect()}
                  >
                    {connecting
                      ? "Opening Google…"
                      : "Enable YouTube publishing"}
                  </button>
                )}
                <Link
                  className="studio-button"
                  to={`${base(brandId)}/youtube/videos?assignment_id=${enc(t.assignment_id)}`}
                >
                  Read videos
                </Link>
              </div>
            </Card>
          ))}
        </div>
      )}
      <div className="studio-notice">
        Google consent, OAuth scope verification, YouTube API compliance review,
        and daily quota approval are separate checks. The interface reports
        permission and project eligibility independently.
      </div>
    </>
  );
}
